import clsx from "clsx";
import React from "react";
import type { DetailData } from "../../../../../contexts/chat-log/chat-log-context";
import styles from "./styles.module.scss";

// YouTube IFrame Player API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export default function YoutubeShorts({
  className,
  file_name,
  asset_title,
  text,
  fullWidth,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"div">, "children"> &
  Pick<DetailData, "file_name" | "asset_title" | "text"> & { fullWidth?: boolean }) {
  const container = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [isMuted, setIsMuted] = React.useState(true); // デフォルトはミュート
  const [showAnimation, setShowAnimation] = React.useState(true);
  // iframe読み込み完了まで内部の黒い背景を隠すためのフラグ
  const [isLoaded, setIsLoaded] = React.useState(false);
  // fullWidth（単独表示）時のみサムネイル+再生ボタンのファサードから開始する
  const [playing, setPlaying] = React.useState(!fullWidth);

  // 4秒後にアニメーションを非表示
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowAnimation(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);

    // postMessageを使用してiframeに直接コマンドを送信
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        const command = newMutedState ? 'mute' : 'unMute';
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: command,
            args: []
          }),
          '*'
        );
      } catch (error) {
        // Error silently handled
      }
    }
  };

  return (
    <>
      < style > {`
        @keyframes whiteFlash {
          0%, 100% {
            border-color: rgba(200, 230, 255, 1);
            box-shadow: 0 0 15px rgba(100, 180, 255, 1), 
                        0 0 30px rgba(150, 210, 255, 1),
                        0 0 45px rgba(180, 220, 255, 0.9),
                        0 0 60px rgba(200, 230, 255, 0.7),
                        inset 0 0 15px rgba(180, 220, 255, 0.8);
          }
          50% {
            border-color: rgba(220, 240, 255, 0.4);
            box-shadow: 0 0 8px rgba(150, 200, 255, 0.6), 
                        0 0 15px rgba(180, 220, 255, 0.5),
                        0 0 22px rgba(200, 230, 255, 0.4),
                        0 0 30px rgba(220, 240, 255, 0.3),
                        inset 0 0 8px rgba(200, 230, 255, 0.4);
          }
        }
        .white-flash-border-shorts {
          animation: whiteFlash 1s ease-in-out infinite;
        }
      `
      }</style >
      {/* \u4e2d\u592e\u63c3\u3048\u30e9\u30c3\u30d1\u30fc */}
      < div style={{ flexShrink: fullWidth ? 1 : 0, flexGrow: fullWidth ? 1 : 0, display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
        < div
          className={clsx(styles.youtube2, styles.shorts, showAnimation && 'white-flash-border-shorts', className)}
          {...props}
          ref={container}
          style={{
            position: 'relative',
            width: fullWidth ? '100%' : '388px', // アセットカード2枚分の幅（190px×2 + gap 8px）
            maxWidth: '100%',
            height: fullWidth ? 'auto' : '690px',
            aspectRatio: fullWidth ? '9 / 16' : undefined,
            border: showAnimation ? "4px solid rgba(200, 230, 255, 1)" : "none",
            borderRadius: "12px",
            overflow: "hidden",
            isolation: "isolate",
            willChange: "auto",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            backgroundColor: '#f0f0f0'
          }}
        >
          {fullWidth && !playing ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', padding: 0, cursor: 'pointer', background: 'none' }}
            >
              <img
                src={`https://img.youtube.com/vi/${file_name}/hqdefault.jpg`}
                alt={asset_title || '動画サムネイル'}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0) 50%)' }} />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '56px',
                  height: '56px',
                  borderRadius: '9999px',
                  background: 'rgba(255, 255, 255, 0.92)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.35)',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#111" style={{ marginLeft: '3px' }}>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              {(asset_title || text) && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 16px', textAlign: 'left' }}>
                  {asset_title && (
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '16px', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                      {asset_title}
                    </div>
                  )}
                  {text && (
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', marginTop: '2px', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                      {text}
                    </div>
                  )}
                </div>
              )}
            </button>
          ) : (
          <>
          {/* ミュートボタン - iframeの上に配置 */}
          < button
            className="shorts-mute-button"
            onClick={toggleMute}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 4,
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title={isMuted ? '音声をオン' : '音声をオフ'}
          >
            {
              isMuted ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
                  <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18.01 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 18.04 14.83 18.45 14 18.7V20.76C15.38 20.45 16.63 19.81 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" fill="currentColor" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12ZM14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z" fill="currentColor" />
                </svg>
              )}
          </button >
          <iframe
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${file_name}?autoplay=0&mute=1&fs=1&controls=1&modestbranding=1&rel=0&enablejsapi=1`}
            title="YouTube Shorts player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            onLoad={() => setIsLoaded(true)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
          ></iframe>
          </>
          )}
        </div >
      </div >
    </>
  );
}
